import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  X, Plus, Edit, Trash2, Calendar, Users, Video, BookOpen, Upload, Mail, FileText, Save,
  Megaphone, MessageCircle, Award, Download, Loader2, Clock, User, MapPin, DollarSign,
  CheckCircle, AlertCircle, Image as ImageIcon, Send, Eye, CheckCircle2, Circle
} from 'lucide-react';
import { 
  addBatchClass, getBatchClasses, updateBatchClass, deleteBatchClass,
  enrollStudentInBatch, getBatchEnrollments, removeBatchEnrollment,
  addBatchStudyMaterial, getBatchStudyMaterials, deleteBatchStudyMaterial,
  getTests, getTimetables, saveTimetable, deleteTimetable,
  getAllStudentDocuments, saveStudentDocument, deleteStudentDocument, getTestById,
  getBatchAnnouncements, getBatchSyllabus, saveBatchSyllabus
} from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import toast from 'react-hot-toast';
import { useRealtime } from '../../contexts/RealtimeContext';
import { collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp, doc, updateDoc, deleteDoc, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const BatchDetailsModalEnhanced = ({ batch, coachingId, onClose, onEdit, onRefresh }) => {
  const { userProfile } = useRealtime();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // State for all sections
  const [batchClasses, setBatchClasses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [batchAnnouncements, setBatchAnnouncements] = useState([]);
  const [batchDoubts, setBatchDoubts] = useState([]);
  const [batchTests, setBatchTests] = useState([]);
  const [batchTimetables, setBatchTimetables] = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [batchAdmitCards, setBatchAdmitCards] = useState([]);
  const [studyMaterials, setStudyMaterials] = useState({});
  const [batchAssignments, setBatchAssignments] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);
  const [batchSyllabusData, setBatchSyllabusData] = useState(null);
  const [isSavingSyllabus, setIsSavingSyllabus] = useState(false);
  
  // Modal states
  const [showClassModal, setShowClassModal] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDoubtModal, setShowDoubtModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [selectedClassForMaterial, setSelectedClassForMaterial] = useState(null);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  // Form states
  const [editingClass, setEditingClass] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [answeringDoubt, setAnsweringDoubt] = useState(null);
  const [editingTimetable, setEditingTimetable] = useState(null);
  const [editingResult, setEditingResult] = useState(null);
  const [availableTests, setAvailableTests] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignmentFormData, setAssignmentFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    maxMarks: 100,
    instructions: ''
  });
  
  const [classFormData, setClassFormData] = useState({
    subject: '', date: '', time: '', duration: 60, zoomLink: '', status: 'scheduled',
    isRevision: false, description: ''
  });
  
  const [enrollmentEmail, setEnrollmentEmail] = useState('');
  
  const [announcementFormData, setAnnouncementFormData] = useState({ title: '', content: '' });
  const [announcementFiles, setAnnouncementFiles] = useState([]);
  const [uploadingAnnouncementFiles, setUploadingAnnouncementFiles] = useState(false);
  
  const [doubtAnswer, setDoubtAnswer] = useState('');
  const [answerImages, setAnswerImages] = useState([]);
  const [uploadingAnswerImages, setUploadingAnswerImages] = useState(false);
  
  const [testFormData, setTestFormData] = useState({ testId: '', testTitle: '' });
  
  const [timetableFormData, setTimetableFormData] = useState({ 
    title: '', 
    description: '', 
    pdfUrl: '',
    type: 'pdf', // 'pdf' or 'structured'
    // For structured entries
    day: '',
    subject: '',
    startTime: '',
    endTime: '',
    teacherName: ''
  });
  const [uploadingTimetable, setUploadingTimetable] = useState(false);
  
  const [resultsFormData, setResultsFormData] = useState({
    studentEmail: '', studentId: '', studentName: '', examName: '', examDate: '',
    documentUrl: '', documentType: 'result', testName: '', exam: '',
    totalMarks: '', marksObtained: '', percentage: '', rank: '',
    subjectWiseMarks: {}
  });
  const [subjectName, setSubjectName] = useState('');
  const [subjectObtained, setSubjectObtained] = useState('');
  const [subjectTotal, setSubjectTotal] = useState('');
  const [uploadingResults, setUploadingResults] = useState(false);
  const [uploadingRecording, setUploadingRecording] = useState(false);

  useEffect(() => {
    if (batch?.id) {
      loadAllData();
    }
  }, [batch?.id]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadBatchClasses(),
        loadEnrollments(),
        loadAvailableTests(),
        loadBatchResults(),
        loadBatchAnnouncements(),
        loadBatchSyllabus()
      ]);
      
      // Set up real-time listeners for doubts, tests, timetables, and assignments
      const unsubDoubts = loadBatchDoubts();
      const unsubTests = loadBatchTests();
      const unsubTimetables = loadBatchTimetables();
      const unsubAssignments = loadBatchAssignments();
      
      return () => {
        if (unsubDoubts) unsubDoubts();
        if (unsubTests) unsubTests();
        if (unsubTimetables) unsubTimetables();
        if (unsubAssignments) unsubAssignments();
      };
    } finally {
      setLoading(false);
    }
  };

  const loadBatchClasses = async () => {
    try {
      const result = await getBatchClasses(batch.id);
      if (result.success) {
        const classes = result.data || [];
        setBatchClasses(classes);
        
        // Load study materials for each class
        const materialsPromises = classes.map(async (classItem) => {
          const materialsResult = await getBatchStudyMaterials(classItem.id);
          return { classId: classItem.id, materials: materialsResult.success ? materialsResult.data : [] };
        });
        
        const materialsData = await Promise.all(materialsPromises);
        const materialsMap = {};
        materialsData.forEach(({ classId, materials }) => {
          materialsMap[classId] = materials;
        });
        setStudyMaterials(materialsMap);
      }
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  };

  const loadEnrollments = async () => {
    try {
      const result = await getBatchEnrollments(batch.id);
      if (result.success) {
        const enrollmentsData = result.data || [];
        
        // Fetch user data for each enrollment if not already present
        const enrichedEnrollments = await Promise.all(
          enrollmentsData.map(async (enrollment) => {
            // If userData is missing or incomplete, try to fetch it
            if (!enrollment.userData?.name && !enrollment.studentName && enrollment.userId) {
              try {
                const userDoc = await getDocs(query(collection(db, 'users'), where('uid', '==', enrollment.userId)));
                if (!userDoc.empty) {
                  const userData = userDoc.docs[0].data();
                  return {
                    ...enrollment,
                    studentName: userData.name || userData.displayName,
                    userData: userData
                  };
                }
              } catch (error) {
                console.error('Error fetching user data:', error);
              }
            }
            return enrollment;
          })
        );
        
        setEnrollments(enrichedEnrollments);
      }
    } catch (error) {
      console.error('Failed to load enrollments:', error);
    }
  };

  const loadBatchAnnouncements = async () => {
    if (!batch?.id) return;
    try {
      const result = await getBatchAnnouncements(batch.id);
      if (result.success) {
        setBatchAnnouncements(result.data);
      } else {
        console.error('Error loading announcements:', result.error);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  const loadBatchSyllabus = async () => {
    if (!batch?.id) return;
    try {
      const result = await getBatchSyllabus(batch.id);
      if (result.success && result.data) {
        setBatchSyllabusData(result.data);
      } else {
        setBatchSyllabusData({ subjects: [] });
      }
    } catch (error) {
      console.error('Error loading syllabus:', error);
    }
  };

  const loadBatchDoubts = () => {
    if (!batch?.id) return;
    const q = query(
      collection(db, 'batchDoubts'),
      where('batchId', '==', batch.id),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBatchDoubts(list);
    }, (error) => console.error('Error loading doubts:', error));
  };

  const loadBatchTests = () => {
    if (!batch?.id) return;
    let q = query(
      collection(db, 'batchTests'),
      where('batchId', '==', batch.id),
      orderBy('addedAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      list.sort((a, b) => {
        const aTime = a.addedAt?.toMillis?.() || new Date(a.addedAt || 0).getTime();
        const bTime = b.addedAt?.toMillis?.() || new Date(b.addedAt || 0).getTime();
        return bTime - aTime;
      });
      setBatchTests(list);
    }, (error) => {
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        const fallbackQ = query(collection(db, 'batchTests'), where('batchId', '==', batch.id));
        return onSnapshot(fallbackQ, (snapshot) => {
          const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          list.sort((a, b) => {
            const aTime = a.addedAt?.toMillis?.() || new Date(a.addedAt || 0).getTime();
            const bTime = b.addedAt?.toMillis?.() || new Date(b.addedAt || 0).getTime();
            return bTime - aTime;
          });
          setBatchTests(list);
        });
      }
    });
  };

  const loadBatchTimetables = () => {
    if (!batch?.id) return;
    console.log('Loading timetables for batch:', batch.id);
    let q = query(
      collection(db, 'timetables'),
      where('batchId', '==', batch.id),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id, ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || 0)
        };
      });
      console.log('Loaded timetables:', list.length, list);
      setBatchTimetables(list);
    }, (error) => {
      console.error('Error loading timetables:', error);
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.log('Using fallback query for timetables');
        const fallbackQ = query(collection(db, 'timetables'), where('batchId', '==', batch.id));
        return onSnapshot(fallbackQ, (snapshot) => {
          const list = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id, ...data,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || 0)
            };
          });
          list.sort((a, b) => {
            const aTime = a.createdAt?.getTime ? a.createdAt.getTime() : (a.createdAt || 0);
            const bTime = b.createdAt?.getTime ? b.createdAt.getTime() : (b.createdAt || 0);
            return bTime - aTime;
          });
          console.log('Loaded timetables (fallback):', list.length, list);
          setBatchTimetables(list);
        });
      }
    });
  };

  const loadBatchAssignments = () => {
    if (!batch?.id) return;
    const q = query(
      collection(db, 'batchAssignments'),
      where('batchId', '==', batch.id),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBatchAssignments(list);
    }, (error) => {
      console.error('Error loading assignments:', error);
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        const fallbackQ = query(collection(db, 'batchAssignments'), where('batchId', '==', batch.id));
        return onSnapshot(fallbackQ, (snapshot) => {
          const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          list.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
            const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
          });
          setBatchAssignments(list);
        });
      }
    });
  };

  const loadAvailableTests = async () => {
    try {
      const result = await getTests({ coachingId, status: 'published' });
      if (result.success) {
        setAvailableTests(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load tests:', error);
    }
  };

  const loadBatchResults = async () => {
    if (!batch?.id) {
      console.log('No batch ID, skipping results load');
      return;
    }
    console.log('Loading results for batch:', batch.id);
    
    try {
      // Query directly without using the API function to avoid index issues
      console.log('Querying studentResults collection...');
      const resultsSnapshot = await getDocs(
        query(collection(db, 'studentResults'), where('batchId', '==', batch.id))
      );
      
      console.log('Querying studentAdmitCards collection...');
      const admitCardsSnapshot = await getDocs(
        query(collection(db, 'studentAdmitCards'), where('batchId', '==', batch.id))
      );
      
      const results = [];
      resultsSnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Found result document:', doc.id, data);
        results.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || 0)
        });
      });
      
      const admitCards = [];
      admitCardsSnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Found admit card document:', doc.id, data);
        admitCards.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || 0)
        });
      });
      
      // Sort by createdAt manually
      results.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
      admitCards.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
      
      console.log('Setting results:', results.length, 'documents');
      console.log('Setting admit cards:', admitCards.length, 'documents');
      
      setBatchResults(results);
      setBatchAdmitCards(admitCards);
      
      if (results.length === 0 && admitCards.length === 0) {
        console.log('No results or admit cards found for this batch');
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load results: ' + error.message);
    }
  };

  // Handler functions for Classes
  const handleClassSubmit = async (e) => {
    e.preventDefault();
    if (!classFormData.subject.trim() || !classFormData.date || !classFormData.time) {
      toast.error('Subject, date, and time are required');
      return;
    }
    try {
      const classData = {
        ...classFormData,
        batchId: batch.id,
        date: new Date(classFormData.date).toISOString().split('T')[0],
        duration: parseInt(classFormData.duration) || 60
      };
      if (editingClass) {
        const result = await updateBatchClass(editingClass.id, classData);
        if (result.success) {
          toast.success('Class updated successfully');
          setShowClassModal(false);
          setEditingClass(null);
          await loadBatchClasses();
        }
      } else {
        const result = await addBatchClass(classData);
        if (result.success) {
          toast.success('Class added successfully');
          setShowClassModal(false);
          await loadBatchClasses();
        }
      }
      setClassFormData({ subject: '', date: '', time: '', duration: 60, zoomLink: '', status: 'scheduled', isRevision: false, description: '' });
    } catch (error) {
      toast.error('Failed to save class');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        const result = await deleteBatchClass(classId);
        if (result.success) {
          toast.success('Class deleted successfully');
          await loadBatchClasses();
        }
      } catch (error) {
        toast.error('Failed to delete class');
      }
    }
  };

  const handleEditClass = (classItem) => {
    setEditingClass(classItem);
    try {
      let dateStr = '';
      if (classItem.date) {
        let dateObj;
        if (classItem.date?.toDate) {
          dateObj = classItem.date.toDate();
        } else if (classItem.date instanceof Date) {
          dateObj = classItem.date;
        } else {
          dateObj = new Date(classItem.date);
        }
        dateStr = dateObj.toISOString().split('T')[0];
      }
      
      setClassFormData({
        subject: classItem.subject || '',
        date: dateStr,
        time: classItem.time || '',
        duration: classItem.duration || 60,
        zoomLink: classItem.zoomLink || '',
        status: classItem.status || 'scheduled',
        isRevision: classItem.isRevision || false,
        description: classItem.description || ''
      });
      setShowClassModal(true);
    } catch (error) {
      toast.error('Failed to load class data');
    }
  };

  const handleRecordingUpload = async (e, classId) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file');
      return;
    }
    try {
      setUploadingRecording(true);
      const fileName = `batch-recordings/${classId}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      if (result.success) {
        await updateBatchClass(classId, { recordingUrl: result.url, status: 'completed' });
        toast.success('Recording uploaded successfully');
        await loadBatchClasses();
      }
    } catch (error) {
      toast.error('Failed to upload recording');
    } finally {
      setUploadingRecording(false);
    }
  };

  const handleMaterialUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploadingMaterial(true);
      const fileName = `batch-materials/${selectedClassForMaterial.id}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      if (result.success) {
        await addBatchStudyMaterial(selectedClassForMaterial.id, {
          title: file.name,
          url: result.url,
          type: file.type,
          size: file.size
        });
        toast.success('Study material uploaded successfully');
        await loadBatchClasses(); // Reload to get updated materials
        setShowMaterialModal(false);
        setSelectedClassForMaterial(null);
      }
    } catch (error) {
      toast.error('Failed to upload material');
    } finally {
      setUploadingMaterial(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this study material?')) return;
    try {
      const result = await deleteBatchStudyMaterial(materialId);
      if (result.success) {
        toast.success('Study material deleted successfully');
        await loadBatchClasses();
      } else {
        toast.error('Failed to delete study material');
      }
    } catch (error) {
      toast.error('Failed to delete study material');
    }
  };

  // Handler functions for Enrollments
  const handleEnrollStudent = async () => {
    if (!enrollmentEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      const result = await enrollStudentInBatch(batch.id, enrollmentEmail);
      if (result.success) {
        toast.success('Student enrolled successfully');
        setEnrollmentEmail('');
        await loadEnrollments();
      } else {
        toast.error(result.error || 'Failed to enroll student');
      }
    } catch (error) {
      toast.error('Failed to enroll student');
    }
  };

  const handleRemoveEnrollment = async (enrollmentId, userId) => {
    if (window.confirm('Are you sure you want to remove this student?')) {
      try {
        const result = await removeBatchEnrollment(batch.id, userId);
        if (result.success) {
          toast.success('Student removed successfully');
          await loadEnrollments();
        }
      } catch (error) {
        toast.error('Failed to remove student');
      }
    }
  };

  // Handler functions for Announcements
  const handleAnnouncementFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not valid. Only images and PDFs allowed.`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max 10MB`);
        continue;
      }
    }
    setUploadingAnnouncementFiles(true);
    const uploadedFiles = [];
    try {
      for (const file of files) {
        const fileName = `announcement_${Date.now()}_${file.name}`;
        const storagePath = `batchAnnouncements/${batch.id}/${userProfile?.uid || userProfile?.id}/${fileName}`;
        const result = await uploadFile(file, storagePath);
        if (result.success) {
          uploadedFiles.push({ url: result.url, name: file.name, type: file.type, size: file.size });
        }
      }
      setAnnouncementFiles(prev => [...prev, ...uploadedFiles]);
      toast.success('Files uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploadingAnnouncementFiles(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementFormData.title.trim() || !announcementFormData.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }
    try {
      await addDoc(collection(db, 'batchAnnouncements'), {
        batchId: batch.id,
        title: announcementFormData.title,
        content: announcementFormData.content,
        files: announcementFiles,
        createdBy: userProfile?.uid || userProfile?.id,
        createdByName: userProfile?.name || userProfile?.email || 'Admin',
        createdAt: serverTimestamp()
      });
      toast.success('Announcement created successfully');
      setAnnouncementFormData({ title: '', content: '' });
      setAnnouncementFiles([]);
      setShowAnnouncementModal(false);
      await loadBatchAnnouncements(); // Refresh announcements
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleUpdateAnnouncement = async (announcementId) => {
    if (!announcementFormData.title.trim() || !announcementFormData.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }
    try {
      await updateDoc(doc(db, 'batchAnnouncements', announcementId), {
        title: announcementFormData.title,
        content: announcementFormData.content,
        files: announcementFiles,
        updatedAt: serverTimestamp()
      });
      toast.success('Announcement updated successfully');
      setAnnouncementFormData({ title: '', content: '' });
      setAnnouncementFiles([]);
      setShowAnnouncementModal(false);
      setEditingAnnouncement(null);
      await loadBatchAnnouncements(); // Refresh announcements
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await deleteDoc(doc(db, 'batchAnnouncements', announcementId));
      toast.success('Announcement deleted successfully');
      await loadBatchAnnouncements(); // Refresh announcements
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  // Handler functions for Doubts
  const handleAnswerImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a valid image`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max 5MB`);
        continue;
      }
    }
    setUploadingAnswerImages(true);
    const uploadedImages = [];
    try {
      for (const file of files) {
        const fileName = `answer_${Date.now()}_${file.name}`;
        const storagePath = `batchDoubts/${batch.id}/${answeringDoubt}/${fileName}`;
        const result = await uploadFile(file, storagePath);
        if (result.success) {
          uploadedImages.push(result.url);
        }
      }
      setAnswerImages(prev => [...prev, ...uploadedImages]);
      toast.success('Images uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingAnswerImages(false);
    }
  };

  const handleAnswerDoubt = async (doubtId) => {
    if (!doubtAnswer.trim() && answerImages.length === 0) {
      toast.error('Please enter an answer or upload an image');
      return;
    }
    try {
      await updateDoc(doc(db, 'batchDoubts', doubtId), {
        answer: doubtAnswer || '',
        answerImages: answerImages,
        answeredBy: userProfile?.uid || userProfile?.id,
        answeredByName: userProfile?.name || userProfile?.email || 'Admin',
        status: 'answered',
        answeredAt: serverTimestamp()
      });
      toast.success('Doubt answered successfully');
      setDoubtAnswer('');
      setAnswerImages([]);
      setAnsweringDoubt(null);
      setShowDoubtModal(false);
    } catch (error) {
      toast.error('Failed to answer doubt');
    }
  };

  const handleCloseDoubt = async (doubtId) => {
    try {
      await updateDoc(doc(db, 'batchDoubts', doubtId), {
        status: 'closed',
        closedAt: serverTimestamp()
      });
      toast.success('Doubt closed successfully');
    } catch (error) {
      toast.error('Failed to close doubt');
    }
  };

  const handleDeleteDoubt = async (doubtId) => {
    if (!window.confirm('Are you sure you want to delete this doubt?')) return;
    try {
      await deleteDoc(doc(db, 'batchDoubts', doubtId));
      toast.success('Doubt deleted successfully');
    } catch (error) {
      toast.error('Failed to delete doubt');
    }
  };

  // Handler functions for Tests
  const handleAddTestToBatch = async () => {
    if (!testFormData.testId.trim()) {
      toast.error('Please enter a test ID');
      return;
    }
    const existingTest = batchTests.find(t => t.testId === testFormData.testId);
    if (existingTest) {
      toast.error('This test is already added to the batch');
      return;
    }
    if (coachingId && batch.coachingId) {
      try {
        const testResult = await getTestById(testFormData.testId);
        if (testResult.success && testResult.data) {
          const test = testResult.data;
          if (test.coachingId === null) {
            toast.error('You cannot add admin-created tests to your batch.');
            return;
          }
          if (test.coachingId !== batch.coachingId) {
            toast.error('This test belongs to a different coaching.');
            return;
          }
        }
      } catch (error) {
        toast.error('Failed to verify test. Please try again.');
        return;
      }
    }
    try {
      await addDoc(collection(db, 'batchTests'), {
        batchId: batch.id,
        testId: testFormData.testId,
        testTitle: testFormData.testTitle || testFormData.testId,
        addedBy: userProfile?.uid || userProfile?.id,
        addedByName: userProfile?.name || userProfile?.email || 'Admin',
        addedAt: serverTimestamp()
      });
      toast.success('Test added to batch successfully');
      setTestFormData({ testId: '', testTitle: '' });
      setShowTestModal(false);
    } catch (error) {
      toast.error('Failed to add test to batch');
    }
  };

  const handleRemoveTestFromBatch = async (batchTestId) => {
    if (!window.confirm('Are you sure you want to remove this test from the batch?')) return;
    try {
      await deleteDoc(doc(db, 'batchTests', batchTestId));
      toast.success('Test removed from batch successfully');
    } catch (error) {
      toast.error('Failed to remove test from batch');
    }
  };

  // Handler functions for Timetables
  const handleTimetableUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    try {
      setUploadingTimetable(true);
      const fileName = `timetables/${batch.id}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      if (result.success) {
        setTimetableFormData(prev => ({ ...prev, pdfUrl: result.url }));
        toast.success('PDF uploaded successfully');
      }
    } catch (error) {
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingTimetable(false);
    }
  };

  const handleSaveTimetable = async () => {
    if (!timetableFormData.title.trim() && timetableFormData.type === 'pdf') {
      toast.error('Please enter a title');
      return;
    }
    
    if (timetableFormData.type === 'structured') {
      if (!timetableFormData.day || !timetableFormData.subject || !timetableFormData.startTime || !timetableFormData.endTime) {
        toast.error('Please fill in all required fields (Day, Subject, Start Time, End Time)');
        return;
      }
    }

    try {
      const timetableData = {
        batchId: batch.id,
        createdBy: userProfile?.uid || userProfile?.id,
        createdByName: userProfile?.name || userProfile?.email || 'Admin'
      };
      
      if (timetableFormData.type === 'pdf') {
        // PDF timetable
        timetableData.title = timetableFormData.title;
        timetableData.description = timetableFormData.description;
        timetableData.pdfUrl = timetableFormData.pdfUrl;
      } else {
        // Structured timetable entry
        timetableData.day = timetableFormData.day;
        timetableData.subject = timetableFormData.subject;
        timetableData.startTime = timetableFormData.startTime;
        timetableData.endTime = timetableFormData.endTime;
        timetableData.teacherName = timetableFormData.teacherName || null;
      }
      
      if (editingTimetable) {
        timetableData.id = editingTimetable.id;
      }
      
      console.log('Saving timetable:', timetableData);
      const result = await saveTimetable(timetableData);
      console.log('Save timetable result:', result);
      
      if (result.success) {
        toast.success(editingTimetable ? 'Timetable updated successfully' : 'Timetable created successfully');
        setShowTimetableModal(false);
        setEditingTimetable(null);
        setTimetableFormData({ 
          title: '', 
          description: '', 
          pdfUrl: '',
          type: 'pdf',
          day: '',
          subject: '',
          startTime: '',
          endTime: '',
          teacherName: ''
        });
      } else {
        toast.error(result.error || 'Failed to save timetable');
      }
    } catch (error) {
      console.error('Error saving timetable:', error);
      toast.error('Failed to save timetable');
    }
  };

  // Handler functions for Results
  const handleResultsUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    try {
      setUploadingResults(true);
      const fileName = `${resultsFormData.documentType}/${batch.id}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      if (result.success) {
        setResultsFormData(prev => ({ ...prev, documentUrl: result.url }));
        toast.success('Document uploaded successfully');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingResults(false);
    }
  };

  const handleSaveResults = async () => {
    // Validate based on document type
    if (resultsFormData.documentType === 'result') {
      if (!resultsFormData.studentEmail.trim() || !resultsFormData.testName.trim() || !resultsFormData.totalMarks || !resultsFormData.marksObtained) {
        toast.error('Please fill in all required fields (Student Email, Test Name, Total Marks, Marks Obtained)');
        return;
      }
    } else {
      if (!resultsFormData.studentEmail.trim() || !resultsFormData.examName.trim()) {
        toast.error('Please fill in all required fields (Student Email, Exam Name)');
        return;
      }
    }
    
    const loadingToast = toast.loading('Saving document...');
    
    try {
      const documentData = {
        ...resultsFormData,
        batchId: batch.id,
        coachingId: batch.coachingId || coachingId || null,
        uploadedBy: userProfile?.uid || userProfile?.id,
        uploadedByName: userProfile?.name || userProfile?.email || 'Admin'
      };
      
      // For results, add score and percentageScore fields for compatibility
      if (resultsFormData.documentType === 'result') {
        documentData.score = parseFloat(resultsFormData.marksObtained);
        documentData.percentageScore = parseFloat(resultsFormData.percentage);
        documentData.totalMarks = parseFloat(resultsFormData.totalMarks);
        documentData.marksObtained = parseFloat(resultsFormData.marksObtained);
        documentData.percentage = parseFloat(resultsFormData.percentage);
        documentData.testName = resultsFormData.testName;
        documentData.exam = resultsFormData.exam || '';
        // Convert rank to number if provided, otherwise set to null
        if (resultsFormData.rank && resultsFormData.rank.toString().trim() !== '') {
          documentData.rank = parseInt(resultsFormData.rank);
        } else {
          documentData.rank = null;
        }
        // Ensure subjectWiseMarks is included
        documentData.subjectWiseMarks = resultsFormData.subjectWiseMarks || {};
      }
      
      if (editingResult) {
        documentData.id = editingResult.id;
      }
      
      console.log('=== SAVING RESULT DOCUMENT ===');
      console.log('Document Type:', documentData.documentType);
      console.log('Test Name:', documentData.testName);
      console.log('Exam Category:', documentData.exam);
      console.log('Total Marks:', documentData.totalMarks);
      console.log('Marks Obtained:', documentData.marksObtained);
      console.log('Percentage:', documentData.percentage);
      console.log('Rank (raw from form):', resultsFormData.rank);
      console.log('Rank (processed):', documentData.rank);
      console.log('Rank type:', typeof documentData.rank);
      console.log('Subject-wise Marks:', JSON.stringify(documentData.subjectWiseMarks, null, 2));
      console.log('Full Document Data:', JSON.stringify(documentData, null, 2));
      
      const result = await saveStudentDocument(documentData);
      
      console.log('=== SAVE RESULT ===');
      console.log('Success:', result.success);
      console.log('Document ID:', result.id);
      if (!result.success) {
        console.error('Error:', result.error);
      }
      
      toast.dismiss(loadingToast);
      
      if (result.success) {
        toast.success(editingResult ? 'Document updated successfully' : 'Document saved successfully');
        setShowResultsModal(false);
        setEditingResult(null);
        setResultsFormData({ studentEmail: '', studentId: '', studentName: '', examName: '', examDate: '', documentUrl: '', documentType: 'result', testName: '', exam: '', totalMarks: '', marksObtained: '', percentage: '', rank: '', subjectWiseMarks: {} });
        setSubjectName('');
        setSubjectObtained('');
        setSubjectTotal('');
        setTimeout(() => {
          loadBatchResults();
        }, 500);
      } else {
        toast.error(result.error || 'Failed to save document');
        console.error('Save failed:', result.error);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error saving document:', error);
      toast.error('Failed to save document: ' + error.message);
    }
  };

  // Handler functions for Assignments
  const handleCreateAssignment = async () => {
    if (!assignmentFormData.title || !assignmentFormData.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const assignmentData = {
        batchId: batch.id,
        teacherId: userProfile?.uid || userProfile?.id,
        ...assignmentFormData,
        status: 'active',
        createdAt: serverTimestamp()
      };

      if (editingAssignment) {
        await updateDoc(doc(db, 'batchAssignments', editingAssignment.id), assignmentData);
        toast.success('Assignment updated successfully');
      } else {
        await addDoc(collection(db, 'batchAssignments'), assignmentData);
        toast.success('Assignment created successfully');
      }

      setShowAssignmentModal(false);
      setEditingAssignment(null);
      setAssignmentFormData({
        title: '',
        description: '',
        dueDate: '',
        maxMarks: 100,
        instructions: ''
      });
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await deleteDoc(doc(db, 'batchAssignments', assignmentId));
      toast.success('Assignment deleted successfully');
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  const loadSubmissions = async (assignment) => {
    setLoadingSubmissions(true);
    setSelectedAssignment(assignment);
    setShowSubmissionsModal(true);

    try {
      const q = query(
        collection(db, 'assignmentSubmissions'),
        where('assignmentId', '==', assignment.id),
        where('batchId', '==', batch.id)
      );
      
      const snapshot = await getDocs(q);
      const submissionsList = [];
      snapshot.forEach((doc) => {
        submissionsList.push({ id: doc.id, ...doc.data() });
      });
      
      // Fetch student emails for submissions that don't have them
      const enhancedSubmissions = await Promise.all(
        submissionsList.map(async (submission) => {
          if (!submission.studentEmail && submission.studentId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', submission.studentId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  ...submission,
                  studentEmail: userData.email || 'No email available',
                  studentName: submission.studentName || userData.displayName || userData.name || 'Unknown Student'
                };
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
            }
          }
          return submission;
        })
      );
      
      // Sort by submittedAt on client side (newest first)
      enhancedSubmissions.sort((a, b) => {
        const aTime = a.submittedAt?.toDate?.() || new Date(a.submittedAt || 0);
        const bTime = b.submittedAt?.toDate?.() || new Date(b.submittedAt || 0);
        return bTime - aTime;
      });
      
      setAssignmentSubmissions(enhancedSubmissions);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const formatSubmissionDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const isSubmissionLate = (submittedAt, dueDate) => {
    if (!submittedAt || !dueDate) return false;
    const submissionDate = submittedAt.toDate ? submittedAt.toDate() : new Date(submittedAt);
    const dueDateObj = new Date(dueDate);
    return submissionDate > dueDateObj;
  };

  // Syllabus Handlers
  const handleAddSubject = () => {
    const name = prompt('Enter subject name:');
    if (!name) return;
    const newSyllabus = { ...batchSyllabusData };
    if (!newSyllabus.subjects) newSyllabus.subjects = [];
    newSyllabus.subjects.push({ name, topics: [] });
    setBatchSyllabusData(newSyllabus);
  };

  const handleAddTopic = (subjectIndex) => {
    const name = prompt('Enter topic name:');
    if (!name) return;
    const newSyllabus = { ...batchSyllabusData };
    newSyllabus.subjects[subjectIndex].topics.push({ name, isDone: false, subtopics: [] });
    setBatchSyllabusData(newSyllabus);
  };

  const handleAddSubtopic = (subjectIndex, topicIndex) => {
    const name = prompt('Enter subtopic name:');
    if (!name) return;
    const newSyllabus = { ...batchSyllabusData };
    newSyllabus.subjects[subjectIndex].topics[topicIndex].subtopics.push({ name, isDone: false });
    setBatchSyllabusData(newSyllabus);
  };

  const handleToggleSubtopic = (subjectIndex, topicIndex, subtopicIndex) => {
    const newSyllabus = { ...batchSyllabusData };
    const subtopic = newSyllabus.subjects[subjectIndex].topics[topicIndex].subtopics[subtopicIndex];
    
    subtopic.isDone = !subtopic.isDone;
    subtopic.doneAt = subtopic.isDone ? new Date().toISOString() : null;

    // Auto-update topic status
    const topic = newSyllabus.subjects[subjectIndex].topics[topicIndex];
    const allSubtopicsDone = topic.subtopics.length > 0 && topic.subtopics.every(s => s.isDone);
    
    topic.isDone = allSubtopicsDone;
    if (allSubtopicsDone) {
      // Find the latest doneAt from subtopics
      const latestDoneAt = topic.subtopics.reduce((latest, s) => {
        if (!latest || (s.doneAt && new Date(s.doneAt) > new Date(latest))) {
          return s.doneAt;
        }
        return latest;
      }, null);
      topic.doneAt = latestDoneAt;
    } else {
      topic.doneAt = null;
    }

    setBatchSyllabusData(newSyllabus);
  };

  const handleDeleteSubject = (index) => {
    if (!window.confirm('Delete this subject and all its topics?')) return;
    const newSyllabus = { ...batchSyllabusData };
    newSyllabus.subjects.splice(index, 1);
    setBatchSyllabusData(newSyllabus);
  };

  const handleDeleteTopic = (subjectIndex, topicIndex) => {
    if (!window.confirm('Delete this topic?')) return;
    const newSyllabus = { ...batchSyllabusData };
    newSyllabus.subjects[subjectIndex].topics.splice(topicIndex, 1);
    setBatchSyllabusData(newSyllabus);
  };

  const handleDeleteSubtopic = (subjectIndex, topicIndex, subtopicIndex) => {
    const newSyllabus = { ...batchSyllabusData };
    newSyllabus.subjects[subjectIndex].topics[topicIndex].subtopics.splice(subtopicIndex, 1);
    
    // Update topic status
    const topic = newSyllabus.subjects[subjectIndex].topics[topicIndex];
    topic.isDone = topic.subtopics.length > 0 && topic.subtopics.every(s => s.isDone);
    
    setBatchSyllabusData(newSyllabus);
  };

  const handleSaveSyllabus = async () => {
    setIsSavingSyllabus(true);
    try {
      const result = await saveBatchSyllabus(batch.id, batchSyllabusData);
      if (result.success) {
        toast.success('Syllabus saved successfully');
        await loadBatchSyllabus();
      } else {
        toast.error('Failed to save syllabus');
      }
    } catch (error) {
      toast.error('Error saving syllabus');
    } finally {
      setIsSavingSyllabus(false);
    }
  };

  const calculateSubjectProgress = (subject) => {
    let totalSubtopics = 0;
    let completedSubtopics = 0;
    
    subject.topics.forEach(topic => {
      totalSubtopics += topic.subtopics.length;
      completedSubtopics += topic.subtopics.filter(s => s.isDone).length;
    });
    
    if (totalSubtopics === 0) return 0;
    return Math.round((completedSubtopics / totalSubtopics) * 100);
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: FileText },
    { id: 'classes', name: 'Classes', icon: Video, badge: batchClasses.length },
    { id: 'students', name: 'Students', icon: Users, badge: enrollments.length },
    { id: 'announcements', name: 'Announcements', icon: Megaphone, badge: batchAnnouncements.length },
    { id: 'doubts', name: 'Doubts', icon: MessageCircle, badge: batchDoubts.length },
    { id: 'syllabus', name: 'Syllabus', icon: BookOpen },
    { id: 'assignments', name: 'Assignments', icon: FileText, badge: batchAssignments.length },
    { id: 'tests', name: 'Tests', icon: FileText, badge: batchTests.length },
    { id: 'timetable', name: 'Timetable', icon: Calendar, badge: batchTimetables.length },
    { id: 'results', name: 'Results', icon: Award, badge: batchResults.length + batchAdmitCards.length },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 z-50 overflow-hidden">
        <div className="w-full h-screen flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900">
                  <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{batch.name}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{batch.description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={onEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 overflow-x-auto bg-white dark:bg-gray-800">
            <nav className="flex space-x-4 px-6 min-w-max">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.name}
                    {tab.badge !== undefined && (
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Students</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{enrollments.length}</p>
                          </div>
                          <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Classes</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{batchClasses.length}</p>
                          </div>
                          <Video className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Tests</p>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{batchTests.length}</p>
                          </div>
                          <FileText className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                        </div>
                      </div>
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Fee</p>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">₹{batch.price || 0}</p>
                          </div>
                          <DollarSign className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Batch Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start space-x-3">
                          <User className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Instructor</p>
                            <p className="font-medium text-gray-900 dark:text-white">{batch.instructorName || 'Not assigned'}</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Duration</p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {batch.startDate && batch.endDate 
                                ? `${new Date(batch.startDate).toLocaleDateString()} - ${new Date(batch.endDate).toLocaleDateString()}`
                                : 'Not set'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Classes Tab - Compact version with essential info */}
                {activeTab === 'classes' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Classes ({batchClasses.length})</h3>
                      <button
                        onClick={() => {
                          setEditingClass(null);
                          setClassFormData({ subject: '', date: '', time: '', duration: 60, zoomLink: '', status: 'scheduled', isRevision: false, description: '' });
                          setShowClassModal(true);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Class</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {batchClasses.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-8">No classes added yet</p>
                      ) : (
                        batchClasses.map((classItem) => (
                          <div key={classItem.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h5 className="font-medium text-gray-900 dark:text-white">{classItem.subject}</h5>
                                  {classItem.isRevision && (
                                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs">Revision</span>
                                  )}
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    classItem.status === 'live' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                    classItem.status === 'completed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                                    'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                  }`}>
                                    {classItem.status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                  {new Date(classItem.date).toLocaleDateString()} at {classItem.time}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <label className={`cursor-pointer p-2 rounded-lg ${classItem.recordingUrl ? 'text-green-600 bg-green-50 dark:bg-green-900/20' : 'text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`} title={classItem.recordingUrl ? 'Recording uploaded - Click to replace' : 'Upload Recording'}>
                                  <Upload className="w-4 h-4" />
                                  <input type="file" accept="video/*" onChange={(e) => handleRecordingUpload(e, classItem.id)} className="hidden" disabled={uploadingRecording} />
                                </label>
                                <button 
                                  onClick={() => {
                                    setSelectedClassForMaterial(classItem);
                                    setShowMaterialModal(true);
                                  }}
                                  className={`p-2 rounded-lg ${studyMaterials[classItem.id]?.length > 0 ? 'text-green-600 bg-green-50 dark:bg-green-900/20' : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                                  title={studyMaterials[classItem.id]?.length > 0 ? `${studyMaterials[classItem.id].length} material(s) uploaded` : 'Upload Study Materials'}
                                >
                                  <BookOpen className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleEditClass(classItem)} className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Edit Class">
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteClass(classItem.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" title="Delete Class">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Show uploaded recording */}
                            {classItem.recordingUrl && (
                              <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <Video className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-green-800 dark:text-green-200">Recording uploaded</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <a href={classItem.recordingUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                                  <button onClick={async () => { if (window.confirm('Remove recording?')) { await updateBatchClass(classItem.id, { recordingUrl: null }); await loadBatchClasses(); toast.success('Recording removed'); } }} className="text-xs text-red-600 hover:underline">Remove</button>
                                </div>
                              </div>
                            )}
                            
                            {/* Show uploaded study materials */}
                            {studyMaterials[classItem.id]?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {studyMaterials[classItem.id].map((material) => (
                                  <div key={material.id} className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
                                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                                      <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                      <span className="text-sm text-blue-800 dark:text-blue-200 truncate">{material.title}</span>
                                    </div>
                                    <div className="flex items-center space-x-2 flex-shrink-0">
                                      <a href={material.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                                      <button onClick={() => handleDeleteMaterial(material.id)} className="text-xs text-red-600 hover:underline">Remove</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Students Tab */}
                {activeTab === 'students' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Enrolled Students ({enrollments.length})</h3>
                      <button
                        onClick={() => setShowEnrollmentModal(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Enroll Student</span>
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {enrollments.length === 0 ? (
                        <div className="col-span-2 text-center py-8 text-gray-500 dark:text-gray-400">No students enrolled yet</div>
                      ) : (
                        enrollments.map((enrollment) => {
                          // Try to get the name from different possible locations
                          const studentName = enrollment.studentName || 
                                            enrollment.userData?.name || 
                                            enrollment.userData?.displayName || 
                                            enrollment.name || 
                                            enrollment.displayName || 
                                            enrollment.email?.split('@')[0] || 
                                            'Unknown User';
                          
                          return (
                            <div key={enrollment.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                  {studentName}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{enrollment.email || enrollment.studentEmail}</p>
                              </div>
                              <button
                                onClick={() => handleRemoveEnrollment(enrollment.id, enrollment.userId || enrollment.studentId)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* Announcements Tab - Compact */}
                {activeTab === 'announcements' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Announcements ({batchAnnouncements.length})</h3>
                      <button onClick={() => { setEditingAnnouncement(null); setAnnouncementFormData({ title: '', content: '' }); setAnnouncementFiles([]); setShowAnnouncementModal(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                        <Plus className="w-4 h-4" /><span>Add</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {batchAnnouncements.length === 0 ? <p className="text-center py-8 text-gray-500">No announcements yet</p> : batchAnnouncements.map((announcement) => (
                        <div key={announcement.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between">
                            <div className="flex-1">
                              <h5 className="font-medium">{announcement.title}</h5>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{announcement.content}</p>
                              {announcement.files && announcement.files.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Attachments:</p>
                                  {announcement.files.map((file, index) => (
                                    <div key={index} className="flex items-center space-x-2">
                                      {file.type && file.type.startsWith('image/') ? (
                                        <ImageIcon className="w-4 h-4 text-blue-600" />
                                      ) : (
                                        <FileText className="w-4 h-4 text-red-600" />
                                      )}
                                      <a
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate"
                                      >
                                        {file.name || `File ${index + 1}`}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-2">By {announcement.createdByName} • {announcement.createdAt?.toDate?.()?.toLocaleDateString()}</p>
                            </div>
                            <div className="flex space-x-2">
                              <button onClick={() => { setEditingAnnouncement(announcement); setAnnouncementFormData({ title: announcement.title, content: announcement.content }); setAnnouncementFiles(announcement.files || []); setShowAnnouncementModal(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit className="w-4 h-4" /></button>
                              <button onClick={() => handleDeleteAnnouncement(announcement.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Doubts Tab - Compact */}
                {activeTab === 'doubts' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Doubts ({batchDoubts.length})</h3>
                    <div className="space-y-2">
                      {batchDoubts.length === 0 ? <p className="text-center py-8 text-gray-500">No doubts yet</p> : batchDoubts.map((doubt) => (
                        <div key={doubt.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-xs px-2 py-1 bg-blue-100 rounded">{doubt.subject || 'General'}</span>
                                <span className={`text-xs px-2 py-1 rounded ${doubt.status === 'answered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{doubt.status || 'pending'}</span>
                              </div>
                              <p className="text-sm font-medium">{doubt.question}</p>
                              {doubt.answer && <div className="mt-2 p-2 bg-green-50 rounded border-l-4 border-green-500"><p className="text-xs font-semibold text-green-800">Answer:</p><p className="text-sm">{doubt.answer}</p></div>}
                            </div>
                            <div className="flex flex-col space-y-2">
                              {!doubt.answer && doubt.status !== 'closed' && <button onClick={() => { setAnsweringDoubt(doubt.id); setDoubtAnswer(''); setAnswerImages([]); setShowDoubtModal(true); }} className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm">Answer</button>}
                              {doubt.status !== 'closed' && <button onClick={() => handleCloseDoubt(doubt.id)} className="px-3 py-1 bg-gray-600 text-white rounded-lg text-sm">Close</button>}
                              <button onClick={() => handleDeleteDoubt(doubt.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assignments Tab */}
                {activeTab === 'assignments' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold">Assignments ({batchAssignments.length})</h3>
                      <button 
                        onClick={() => {
                          setEditingAssignment(null);
                          setAssignmentFormData({
                            title: '',
                            description: '',
                            dueDate: '',
                            maxMarks: 100,
                            instructions: ''
                          });
                          setShowAssignmentModal(true);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Create Assignment</span>
                      </button>
                    </div>

                    {batchAssignments.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Assignments Created</h3>
                        <p className="text-gray-600 mb-4">Create your first assignment for this batch</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {batchAssignments.map((assignment) => (
                          <div key={assignment.id} className="bg-white border rounded-lg p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <h4 className="text-lg font-semibold text-gray-900">{assignment.title}</h4>
                                <p className="text-gray-600 mt-2">{assignment.description}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-3">
                                  <span>Due: {assignment.dueDate}</span>
                                  <span>Max Marks: {assignment.maxMarks}</span>
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    assignment.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {assignment.status}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => loadSubmissions(assignment)}
                                  className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg text-sm flex items-center"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View Submissions
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingAssignment(assignment);
                                    setAssignmentFormData({
                                      title: assignment.title,
                                      description: assignment.description,
                                      dueDate: assignment.dueDate,
                                      maxMarks: assignment.maxMarks,
                                      instructions: assignment.instructions || ''
                                    });
                                    setShowAssignmentModal(true);
                                  }}
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAssignment(assignment.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Tests Tab - Compact */}
                {activeTab === 'tests' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Tests ({batchTests.length})</h3>
                      <button onClick={() => { setTestFormData({ testId: '', testTitle: '' }); setShowTestModal(true); loadAvailableTests(); }} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2">
                        <Plus className="w-4 h-4" /><span>Add Test</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {batchTests.length === 0 ? <p className="text-center py-8 text-gray-500">No tests added yet</p> : batchTests.map((batchTest) => (
                        <div key={batchTest.id} className="p-4 border rounded-lg flex items-center justify-between">
                          <div>
                            <h5 className="font-medium">{batchTest.testTitle || batchTest.testId}</h5>
                            <p className="text-xs text-gray-500">Test ID: {batchTest.testId}</p>
                          </div>
                          <button onClick={() => handleRemoveTestFromBatch(batchTest.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timetable Tab - Compact */}
                {activeTab === 'timetable' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Timetable ({batchTimetables.length})</h3>
                      <button onClick={() => { setEditingTimetable(null); setTimetableFormData({ title: '', description: '', pdfUrl: '', type: 'pdf', day: '', subject: '', startTime: '', endTime: '', teacherName: '' }); setShowTimetableModal(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                        <Plus className="w-4 h-4" /><span>Add</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {batchTimetables.length === 0 ? <p className="text-center py-8 text-gray-500">No timetables added yet</p> : batchTimetables.map((timetable) => {
                        const isPdf = timetable.pdfUrl && (!timetable.day && !timetable.subject);
                        return (
                          <div key={timetable.id} className="p-4 border rounded-lg flex items-center justify-between">
                            <div className="flex-1">
                              {isPdf ? (
                                <>
                                  <div className="flex items-center space-x-2">
                                    <FileText className="w-5 h-5 text-red-600" />
                                    <h5 className="font-medium">{timetable.title}</h5>
                                  </div>
                                  {timetable.description && <p className="text-xs text-gray-500 mt-1">{timetable.description}</p>}
                                  {timetable.pdfUrl && <a href={timetable.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"><Download className="w-3 h-3" />View PDF</a>}
                                </>
                              ) : (
                                <>
                                  <div className="flex items-center space-x-2">
                                    <Calendar className="w-5 h-5 text-blue-600" />
                                    <h5 className="font-medium">{timetable.day} - {timetable.subject}</h5>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{timetable.startTime} - {timetable.endTime}</p>
                                  {timetable.teacherName && <p className="text-xs text-gray-500 mt-1">Teacher: {timetable.teacherName}</p>}
                                </>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <button onClick={() => { setEditingTimetable(timetable); setTimetableFormData({ title: timetable.title || '', description: timetable.description || '', pdfUrl: timetable.pdfUrl || '', type: isPdf ? 'pdf' : 'structured', day: timetable.day || '', subject: timetable.subject || '', startTime: timetable.startTime || '', endTime: timetable.endTime || '', teacherName: timetable.teacherName || '' }); setShowTimetableModal(true); }} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4" /></button>
                              <button onClick={async () => { if (window.confirm('Delete this timetable?')) { const result = await deleteTimetable(timetable.id); if (result.success) toast.success('Timetable deleted'); } }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Syllabus Tab */}
                {activeTab === 'syllabus' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Batch Syllabus</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleAddSubject}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Subject</span>
                        </button>
                        <button
                          onClick={handleSaveSyllabus}
                          disabled={isSavingSyllabus}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50"
                        >
                          {isSavingSyllabus ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          <span>Save Syllabus</span>
                        </button>
                      </div>
                    </div>

                    {!batchSyllabusData || !batchSyllabusData.subjects || batchSyllabusData.subjects.length === 0 ? (
                      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                        <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">No syllabus created yet. Start by adding a subject.</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {batchSyllabusData.subjects.map((subject, sIdx) => {
                          const progress = calculateSubjectProgress(subject);
                          return (
                            <div key={sIdx} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">{subject.name}</h4>
                                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold rounded-full">
                                      {progress}% Done
                                    </span>
                                  </div>
                                  <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full transition-all duration-500" 
                                      style={{ width: `${progress}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                  <button
                                    onClick={() => handleAddTopic(sIdx)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                                    title="Add Topic"
                                  >
                                    <Plus className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSubject(sIdx)}
                                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                    title="Delete Subject"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                              <div className="p-4 space-y-4">
                                {subject.topics.map((topic, tIdx) => (
                                  <div key={tIdx} className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                                    <div className="p-3 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between">
                                      <div className="flex items-center space-x-2">
                                        {topic.isDone ? (
                                          <CheckCircle className="w-5 h-5 text-green-500" />
                                        ) : (
                                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                                        )}
                                        <div className="flex flex-col">
                                          {topic.isDone && topic.doneAt && (
                                            <span className="text-[10px] text-green-600 dark:text-green-400 font-medium mb-0.5">
                                              Done: {new Date(topic.doneAt).toLocaleDateString('en-IN', {
                                                day: 'numeric',
                                                month: 'short',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </span>
                                          )}
                                          <span className={`font-semibold ${topic.isDone ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                                            {topic.name}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <button
                                          onClick={() => handleAddSubtopic(sIdx, tIdx)}
                                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md"
                                          title="Add Subtopic"
                                        >
                                          <Plus className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteTopic(sIdx, tIdx)}
                                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="p-3 bg-white dark:bg-gray-800 grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {topic.subtopics.map((subtopic, stIdx) => (
                                        <div key={stIdx} className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                                          <div className="flex items-center space-x-3">
                                            <input
                                              type="checkbox"
                                              checked={subtopic.isDone}
                                              onChange={() => handleToggleSubtopic(sIdx, tIdx, stIdx)}
                                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className={`text-sm ${subtopic.isDone ? 'text-gray-500 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                                              {subtopic.name}
                                            </span>
                                          </div>
                                          <button
                                            onClick={() => handleDeleteSubtopic(sIdx, tIdx, stIdx)}
                                            className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ))}
                                      {topic.subtopics.length === 0 && (
                                        <p className="text-xs text-gray-400 italic col-span-full">No subtopics added</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {subject.topics.length === 0 && (
                                  <p className="text-sm text-gray-400 text-center py-4">No topics added to this subject</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Results Tab - Compact */}
                {activeTab === 'results' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Results & Admit Cards ({batchResults.length + batchAdmitCards.length})</h3>
                      <button onClick={() => { 
                        setEditingResult(null); 
                        setResultsFormData({ 
                          studentEmail: '', studentId: '', studentName: '', examName: '', examDate: '', 
                          documentUrl: '', documentType: 'result', testName: '', exam: '', 
                          totalMarks: '', marksObtained: '', percentage: '', rank: '', subjectWiseMarks: {} 
                        }); 
                        setSubjectName('');
                        setSubjectObtained('');
                        setSubjectTotal('');
                        setShowResultsModal(true); 
                      }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2">
                        <Plus className="w-4 h-4" /><span>Add</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {batchResults.length === 0 && batchAdmitCards.length === 0 ? <p className="text-center py-8 text-gray-500">No documents added yet</p> : (
                        <>
                          {batchResults.map((result) => (
                            <div key={result.id} className="p-4 border rounded-lg">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h5 className="font-medium">{result.studentName || result.studentEmail}</h5>
                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">Result</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">{result.examName || result.testName} • {result.studentEmail}</p>
                                  {result.documentUrl && <a href={result.documentUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"><Download className="w-3 h-3" />View</a>}
                                </div>
                                <div className="flex space-x-2">
                                  <button onClick={() => { 
                                    setEditingResult(result); 
                                    setResultsFormData({ 
                                      studentEmail: result.studentEmail || '', 
                                      studentId: result.studentId || '', 
                                      studentName: result.studentName || '', 
                                      examName: result.examName || result.testName || '', 
                                      examDate: result.examDate || '', 
                                      documentUrl: result.documentUrl || '', 
                                      documentType: 'result',
                                      testName: result.testName || result.examName || '',
                                      exam: result.exam || '',
                                      totalMarks: result.totalMarks || '',
                                      marksObtained: result.marksObtained || result.score || '',
                                      percentage: result.percentage || result.percentageScore || '',
                                      rank: result.rank !== undefined && result.rank !== null ? result.rank : '',
                                      subjectWiseMarks: result.subjectWiseMarks || {}
                                    }); 
                                    setSubjectName('');
                                    setSubjectObtained('');
                                    setSubjectTotal('');
                                    setShowResultsModal(true); 
                                  }} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4" /></button>
                                  <button onClick={async () => { if (window.confirm('Delete this document?')) { const res = await deleteStudentDocument(result.id, 'result'); if (res.success) { toast.success('Document deleted'); await loadBatchResults(); } } }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </div>
                              
                              {/* Display marks info */}
                              {(result.totalMarks || result.marksObtained || result.percentage) && (
                                <div className="flex items-center gap-3 mb-2 text-sm">
                                  {result.marksObtained && result.totalMarks && (
                                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                                      Marks: {result.marksObtained}/{result.totalMarks}
                                    </span>
                                  )}
                                  {result.percentage && (
                                    <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded font-semibold">
                                      {result.percentage}%
                                    </span>
                                  )}
                                  {result.rank && (
                                    <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded">
                                      Rank: {result.rank}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Display subject-wise marks */}
                              {result.subjectWiseMarks && Object.keys(result.subjectWiseMarks).length > 0 && (
                                <div className="mt-3 pt-3 border-t">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Subject-wise Performance:</p>
                                  <div className="grid grid-cols-1 gap-2">
                                    {Object.entries(result.subjectWiseMarks).map(([subject, marks]) => {
                                      const isOldFormat = typeof marks === 'number';
                                      const obtained = isOldFormat ? marks : marks.obtained;
                                      const total = isOldFormat ? null : marks.total;
                                      const percentage = total ? ((obtained / total) * 100).toFixed(1) : null;
                                      
                                      const getPerformanceLabel = (pct) => {
                                        if (!pct) return null;
                                        if (pct >= 90) return { label: 'Excellent', bgColor: 'bg-green-100', textColor: 'text-green-700' };
                                        if (pct >= 75) return { label: 'Good', bgColor: 'bg-blue-100', textColor: 'text-blue-700' };
                                        if (pct >= 60) return { label: 'Average', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' };
                                        if (pct >= 40) return { label: 'Need Improvement', bgColor: 'bg-orange-100', textColor: 'text-orange-700' };
                                        return { label: 'Please Revise', bgColor: 'bg-red-100', textColor: 'text-red-700' };
                                      };
                                      
                                      const performance = percentage ? getPerformanceLabel(parseFloat(percentage)) : null;
                                      
                                      return (
                                        <div key={subject} className="flex items-center justify-between text-xs bg-gray-50 rounded p-2">
                                          <span className="font-medium">{subject}</span>
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">
                                              {obtained}{total ? `/${total}` : ''}
                                            </span>
                                            {percentage && (
                                              <span className="text-gray-600">({percentage}%)</span>
                                            )}
                                            {performance && (
                                              <span className={`px-2 py-0.5 rounded ${performance.bgColor} ${performance.textColor}`}>
                                                {performance.label}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                          {batchAdmitCards.map((admitCard) => (
                            <div key={admitCard.id} className="p-4 border rounded-lg flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h5 className="font-medium">{admitCard.studentName || admitCard.studentEmail}</h5>
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">Admit Card</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{admitCard.examName} • {admitCard.studentEmail}</p>
                                {admitCard.documentUrl && <a href={admitCard.documentUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"><Download className="w-3 h-3" />View</a>}
                              </div>
                              <div className="flex space-x-2">
                                <button onClick={() => { 
                                  setEditingResult(admitCard); 
                                  setResultsFormData({ 
                                    studentEmail: admitCard.studentEmail || '', 
                                    studentId: admitCard.studentId || '', 
                                    studentName: admitCard.studentName || '', 
                                    examName: admitCard.examName || '', 
                                    examDate: admitCard.examDate || '', 
                                    documentUrl: admitCard.documentUrl || '', 
                                    documentType: 'admitCard',
                                    testName: '',
                                    exam: '',
                                    totalMarks: '',
                                    marksObtained: '',
                                    percentage: '',
                                    rank: '',
                                    subjectWiseMarks: {}
                                  }); 
                                  setSubjectName('');
                                  setSubjectObtained('');
                                  setSubjectTotal('');
                                  setShowResultsModal(true); 
                                }} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4" /></button>
                                <button onClick={async () => { if (window.confirm('Delete this document?')) { const res = await deleteStudentDocument(admitCard.id, 'admitCard'); if (res.success) { toast.success('Document deleted'); await loadBatchResults(); } } }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Study Material Upload Modal */}
      {showMaterialModal && selectedClassForMaterial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Upload Study Material
              </h3>
              <button
                onClick={() => {
                  setShowMaterialModal(false);
                  setSelectedClassForMaterial(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Class: <span className="font-medium text-gray-900 dark:text-white">{selectedClassForMaterial.subject}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {new Date(selectedClassForMaterial.date).toLocaleDateString()} at {selectedClassForMaterial.time}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload File
                </label>
                <input
                  type="file"
                  onChange={handleMaterialUpload}
                  disabled={uploadingMaterial}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                />
                {uploadingMaterial && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 flex items-center">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Uploading...
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Supported formats: PDF, DOC, DOCX, PPT, PPTX, JPG, PNG
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Class Modal */}
      {showClassModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingClass ? 'Edit Class' : 'Add New Class'}
              </h3>
              <button
                onClick={() => {
                  setShowClassModal(false);
                  setEditingClass(null);
                  setClassFormData({ subject: '', date: '', time: '', duration: 60, zoomLink: '', status: 'scheduled', isRevision: false, description: '' });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleClassSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subject *</label>
                  <input
                    type="text"
                    value={classFormData.subject}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date *</label>
                  <input
                    type="date"
                    value={classFormData.date}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time *</label>
                  <input
                    type="time"
                    value={classFormData.time}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Duration (minutes)</label>
                  <input
                    type="number"
                    value={classFormData.duration}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    min="15"
                    step="15"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Zoom Link</label>
                  <input
                    type="url"
                    value={classFormData.zoomLink}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, zoomLink: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="https://zoom.us/j/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                  <select
                    value={classFormData.status}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="live">Live</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={classFormData.isRevision}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, isRevision: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Revision Class</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                <textarea
                  value={classFormData.description}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowClassModal(false);
                    setEditingClass(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingClass ? 'Update' : 'Add'} Class</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enroll Student Modal */}
      {showEnrollmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Enroll Student
              </h3>
              <button
                onClick={() => {
                  setShowEnrollmentModal(false);
                  setEnrollmentEmail('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student Email Address *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    value={enrollmentEmail}
                    onChange={(e) => setEnrollmentEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleEnrollStudent()}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="student@example.com"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowEnrollmentModal(false);
                    setEnrollmentEmail('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEnrollStudent}
                  disabled={!enrollmentEmail.trim()}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>Enroll</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingAnnouncement ? 'Edit Announcement' : 'Add Announcement'}
              </h3>
              <button
                onClick={() => {
                  setShowAnnouncementModal(false);
                  setEditingAnnouncement(null);
                  setAnnouncementFormData({ title: '', content: '' });
                  setAnnouncementFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
                <input
                  type="text"
                  value={announcementFormData.title}
                  onChange={(e) => setAnnouncementFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Announcement title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content *</label>
                <textarea
                  value={announcementFormData.content}
                  onChange={(e) => setAnnouncementFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  rows={5}
                  placeholder="Announcement content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attachments</label>
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  onChange={handleAnnouncementFileUpload}
                  disabled={uploadingAnnouncementFiles}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {uploadingAnnouncementFiles && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 flex items-center">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Uploading files...
                  </p>
                )}
                {announcementFiles.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {announcementFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                        <button
                          onClick={() => setAnnouncementFiles(prev => prev.filter((_, i) => i !== index))}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAnnouncementModal(false);
                    setEditingAnnouncement(null);
                    setAnnouncementFormData({ title: '', content: '' });
                    setAnnouncementFiles([]);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => editingAnnouncement ? handleUpdateAnnouncement(editingAnnouncement.id) : handleCreateAnnouncement()}
                  disabled={!announcementFormData.title.trim() || !announcementFormData.content.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{editingAnnouncement ? 'Update' : 'Post'} Announcement</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Answer Doubt Modal */}
      {showDoubtModal && answeringDoubt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Answer Doubt</h3>
              <button
                onClick={() => {
                  setShowDoubtModal(false);
                  setAnsweringDoubt(null);
                  setDoubtAnswer('');
                  setAnswerImages([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Answer</label>
                <textarea
                  value={doubtAnswer}
                  onChange={(e) => setDoubtAnswer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  rows={5}
                  placeholder="Type your answer here..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attach Images (Optional)</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleAnswerImageUpload}
                  disabled={uploadingAnswerImages}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {uploadingAnswerImages && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 flex items-center">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Uploading images...
                  </p>
                )}
                {answerImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {answerImages.map((imageUrl, index) => (
                      <div key={index} className="relative">
                        <img src={imageUrl} alt={`Answer ${index + 1}`} className="w-full h-24 object-cover rounded" />
                        <button
                          onClick={() => setAnswerImages(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowDoubtModal(false);
                    setAnsweringDoubt(null);
                    setDoubtAnswer('');
                    setAnswerImages([]);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAnswerDoubt(answeringDoubt)}
                  disabled={!doubtAnswer.trim() && answerImages.length === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Submit Answer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Test Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Test to Batch</h3>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestFormData({ testId: '', testTitle: '' });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Test ID *</label>
                <input
                  type="text"
                  value={testFormData.testId}
                  onChange={(e) => setTestFormData(prev => ({ ...prev, testId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter test ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Test Title (Optional)</label>
                <input
                  type="text"
                  value={testFormData.testTitle}
                  onChange={(e) => setTestFormData(prev => ({ ...prev, testTitle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter test title"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowTestModal(false);
                    setTestFormData({ testId: '', testTitle: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTestToBatch}
                  disabled={!testFormData.testId.trim()}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Test</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Results/Admit Card Modal */}
      {showResultsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingResult ? 'Edit Document' : 'Add Result/Admit Card'}
              </h3>
              <button
                onClick={() => {
                  setShowResultsModal(false);
                  setEditingResult(null);
                  setResultsFormData({ studentEmail: '', studentId: '', studentName: '', examName: '', examDate: '', documentUrl: '', documentType: 'result', testName: '', exam: '', totalMarks: '', marksObtained: '', percentage: '', rank: '', subjectWiseMarks: {} });
                  setSubjectName('');
                  setSubjectObtained('');
                  setSubjectTotal('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Document Type *</label>
                <select
                  value={resultsFormData.documentType}
                  onChange={(e) => setResultsFormData(prev => ({ ...prev, documentType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="result">Result</option>
                  <option value="admitCard">Admit Card</option>
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Student Email *</label>
                  <input
                    type="email"
                    value={resultsFormData.studentEmail}
                    onChange={(e) => setResultsFormData(prev => ({ ...prev, studentEmail: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="student@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Student Name</label>
                  <input
                    type="text"
                    value={resultsFormData.studentName}
                    onChange={(e) => setResultsFormData(prev => ({ ...prev, studentName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Student name"
                  />
                </div>
              </div>
              
              {resultsFormData.documentType === 'result' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Test Name *</label>
                      <input
                        type="text"
                        value={resultsFormData.testName}
                        onChange={(e) => setResultsFormData(prev => ({ ...prev, testName: e.target.value, examName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., Mid-term Exam"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Exam Category</label>
                      <input
                        type="text"
                        value={resultsFormData.exam}
                        onChange={(e) => setResultsFormData(prev => ({ ...prev, exam: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., JEE, NEET"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Total Marks *</label>
                      <input
                        type="number"
                        value={resultsFormData.totalMarks}
                        onChange={(e) => {
                          const total = parseFloat(e.target.value) || 0;
                          const obtained = parseFloat(resultsFormData.marksObtained) || 0;
                          const percentage = total > 0 ? ((obtained / total) * 100).toFixed(2) : '';
                          setResultsFormData(prev => ({ ...prev, totalMarks: e.target.value, percentage }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Marks Obtained *</label>
                      <input
                        type="number"
                        value={resultsFormData.marksObtained}
                        onChange={(e) => {
                          const obtained = parseFloat(e.target.value) || 0;
                          const total = parseFloat(resultsFormData.totalMarks) || 0;
                          const percentage = total > 0 ? ((obtained / total) * 100).toFixed(2) : '';
                          setResultsFormData(prev => ({ ...prev, marksObtained: e.target.value, percentage }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="85"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Percentage</label>
                      <input
                        type="text"
                        value={resultsFormData.percentage}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 dark:text-white"
                        placeholder="Auto-calculated"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rank</label>
                      <input
                        type="number"
                        value={resultsFormData.rank}
                        onChange={(e) => setResultsFormData(prev => ({ ...prev, rank: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., 1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Exam Date</label>
                      <input
                        type="date"
                        value={resultsFormData.examDate}
                        onChange={(e) => setResultsFormData(prev => ({ ...prev, examDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                  
                  {/* Subject-wise Marks */}
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Subject-wise Marks (Optional)</label>
                    
                    <div className="grid grid-cols-12 gap-2 mb-3">
                      <input
                        type="text"
                        value={subjectName}
                        onChange={(e) => setSubjectName(e.target.value)}
                        className="col-span-5 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Subject (e.g., Mathematics)"
                      />
                      <input
                        type="number"
                        value={subjectObtained}
                        onChange={(e) => setSubjectObtained(e.target.value)}
                        className="col-span-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Obtained"
                      />
                      <input
                        type="number"
                        value={subjectTotal}
                        onChange={(e) => setSubjectTotal(e.target.value)}
                        className="col-span-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Total"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (subjectName.trim() && subjectObtained.trim() && subjectTotal.trim()) {
                            setResultsFormData(prev => ({
                              ...prev,
                              subjectWiseMarks: {
                                ...prev.subjectWiseMarks,
                                [subjectName.trim()]: {
                                  obtained: parseFloat(subjectObtained),
                                  total: parseFloat(subjectTotal)
                                }
                              }
                            }));
                            setSubjectName('');
                            setSubjectObtained('');
                            setSubjectTotal('');
                          }
                        }}
                        className="col-span-1 px-2 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {resultsFormData.subjectWiseMarks && Object.keys(resultsFormData.subjectWiseMarks).length > 0 && (
                      <div className="space-y-2">
                        {Object.entries(resultsFormData.subjectWiseMarks).map(([subject, marks]) => {
                          const isOldFormat = typeof marks === 'number';
                          const obtained = isOldFormat ? marks : marks.obtained;
                          const total = isOldFormat ? null : marks.total;
                          const percentage = total ? ((obtained / total) * 100).toFixed(1) : null;
                          
                          // Get performance label and color
                          const getPerformanceLabel = (pct) => {
                            if (!pct) return null;
                            if (pct >= 90) return { label: 'Excellent', bgColor: 'bg-green-100', textColor: 'text-green-700', borderColor: 'border-green-300' };
                            if (pct >= 75) return { label: 'Good', bgColor: 'bg-blue-100', textColor: 'text-blue-700', borderColor: 'border-blue-300' };
                            if (pct >= 60) return { label: 'Average', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', borderColor: 'border-yellow-300' };
                            if (pct >= 40) return { label: 'Need Improvement', bgColor: 'bg-orange-100', textColor: 'text-orange-700', borderColor: 'border-orange-300' };
                            return { label: 'Please Revise the Topics', bgColor: 'bg-red-100', textColor: 'text-red-700', borderColor: 'border-red-300' };
                          };
                          
                          const performance = percentage ? getPerformanceLabel(parseFloat(percentage)) : null;
                          
                          return (
                            <div key={subject} className={`flex flex-col bg-gray-50 dark:bg-gray-700 rounded-md p-3 border ${performance ? performance.borderColor : 'border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{subject}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                                    {obtained}{total ? `/${total}` : ''}
                                  </span>
                                  {percentage && (
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                      ({percentage}%)
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newSubjects = { ...resultsFormData.subjectWiseMarks };
                                      delete newSubjects[subject];
                                      setResultsFormData(prev => ({ ...prev, subjectWiseMarks: newSubjects }));
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              {performance && (
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                                    <div 
                                      className={`h-full ${performance.bgColor.replace('bg-', 'bg-').replace('-100', '-500')}`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${performance.bgColor} ${performance.textColor}`}>
                                    {performance.label}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {resultsFormData.documentType === 'admitCard' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Exam Name *</label>
                    <input
                      type="text"
                      value={resultsFormData.examName}
                      onChange={(e) => setResultsFormData(prev => ({ ...prev, examName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Mid-term Exam"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Exam Date</label>
                    <input
                      type="date"
                      value={resultsFormData.examDate}
                      onChange={(e) => setResultsFormData(prev => ({ ...prev, examDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Upload Document (Optional)</label>
                {resultsFormData.documentUrl ? (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-800 dark:text-green-200">Document uploaded</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a href={resultsFormData.documentUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                      <button onClick={() => setResultsFormData(prev => ({ ...prev, documentUrl: '' }))} className="text-xs text-red-600 hover:underline">Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      onChange={handleResultsUpload}
                      disabled={uploadingResults}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    {uploadingResults && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 flex items-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Uploading document...
                      </p>
                    )}
                  </>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowResultsModal(false);
                    setEditingResult(null);
                    setResultsFormData({ studentEmail: '', studentId: '', studentName: '', examName: '', examDate: '', documentUrl: '', documentType: 'result', testName: '', exam: '', totalMarks: '', marksObtained: '', percentage: '', rank: '', subjectWiseMarks: {} });
                    setSubjectName('');
                    setSubjectObtained('');
                    setSubjectTotal('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveResults}
                  disabled={!resultsFormData.studentEmail.trim() || (resultsFormData.documentType === 'result' ? (!resultsFormData.testName.trim() || !resultsFormData.totalMarks || !resultsFormData.marksObtained) : !resultsFormData.examName.trim()) || uploadingResults}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingResult ? 'Update' : 'Add'} Document</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timetable Upload Modal */}
      {showTimetableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingTimetable ? 'Edit Timetable' : 'Add Timetable'}
              </h3>
              <button
                onClick={() => {
                  setShowTimetableModal(false);
                  setEditingTimetable(null);
                  setTimetableFormData({ 
                    title: '', 
                    description: '', 
                    pdfUrl: '',
                    type: 'pdf',
                    day: '',
                    subject: '',
                    startTime: '',
                    endTime: '',
                    teacherName: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timetable Type
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => setTimetableFormData(prev => ({ ...prev, type: 'pdf' }))}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                    timetableFormData.type === 'pdf'
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <FileText className="w-5 h-5" />
                    <span className="font-medium">PDF Document</span>
                  </div>
                  <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">Upload a complete timetable PDF</p>
                </button>
                <button
                  onClick={() => setTimetableFormData(prev => ({ ...prev, type: 'structured' }))}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                    timetableFormData.type === 'structured'
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Calendar className="w-5 h-5" />
                    <span className="font-medium">Class Entry</span>
                  </div>
                  <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">Add individual class schedule</p>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {timetableFormData.type === 'pdf' ? (
                <>
                  {/* PDF Timetable Fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={timetableFormData.title}
                      onChange={(e) => setTimetableFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Weekly Timetable"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={timetableFormData.description}
                      onChange={(e) => setTimetableFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      rows={3}
                      placeholder="Optional description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload PDF
                    </label>
                    {timetableFormData.pdfUrl ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-green-800 dark:text-green-200">PDF uploaded</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a href={timetableFormData.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                          <button onClick={() => setTimetableFormData(prev => ({ ...prev, pdfUrl: '' }))} className="text-xs text-red-600 hover:underline">Remove</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleTimetableUpload}
                          disabled={uploadingTimetable}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                        {uploadingTimetable && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 flex items-center">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Uploading PDF...
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Structured Timetable Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Day *
                      </label>
                      <select
                        value={timetableFormData.day}
                        onChange={(e) => setTimetableFormData(prev => ({ ...prev, day: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Select Day</option>
                        <option value="Monday">Monday</option>
                        <option value="Tuesday">Tuesday</option>
                        <option value="Wednesday">Wednesday</option>
                        <option value="Thursday">Thursday</option>
                        <option value="Friday">Friday</option>
                        <option value="Saturday">Saturday</option>
                        <option value="Sunday">Sunday</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        value={timetableFormData.subject}
                        onChange={(e) => setTimetableFormData(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., Mathematics"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Start Time *
                      </label>
                      <input
                        type="time"
                        value={timetableFormData.startTime}
                        onChange={(e) => setTimetableFormData(prev => ({ ...prev, startTime: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        End Time *
                      </label>
                      <input
                        type="time"
                        value={timetableFormData.endTime}
                        onChange={(e) => setTimetableFormData(prev => ({ ...prev, endTime: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Teacher Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={timetableFormData.teacherName}
                      onChange={(e) => setTimetableFormData(prev => ({ ...prev, teacherName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., John Doe"
                    />
                  </div>
                </>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowTimetableModal(false);
                    setEditingTimetable(null);
                    setTimetableFormData({ 
                      title: '', 
                      description: '', 
                      pdfUrl: '',
                      type: 'pdf',
                      day: '',
                      subject: '',
                      startTime: '',
                      endTime: '',
                      teacherName: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTimetable}
                  disabled={uploadingTimetable}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingTimetable ? 'Update' : 'Add'} {timetableFormData.type === 'pdf' ? 'Timetable' : 'Class'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals would go here - keeping them minimal for now */}
      {/* You can add the full modal implementations from BatchManagement.js */}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingAssignment ? 'Edit Assignment' : 'Create Assignment'}
              </h3>
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setEditingAssignment(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={assignmentFormData.title}
                  onChange={(e) => setAssignmentFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={assignmentFormData.description}
                  onChange={(e) => setAssignmentFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                  <input
                    type="date"
                    value={assignmentFormData.dueDate}
                    onChange={(e) => setAssignmentFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Marks</label>
                  <input
                    type="number"
                    value={assignmentFormData.maxMarks}
                    onChange={(e) => setAssignmentFormData(prev => ({ ...prev, maxMarks: parseInt(e.target.value) || 100 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                <textarea
                  value={assignmentFormData.instructions}
                  onChange={(e) => setAssignmentFormData(prev => ({ ...prev, instructions: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setEditingAssignment(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAssignment}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingAssignment ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {showSubmissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">Assignment Submissions</h3>
                <p className="text-sm text-gray-600">{selectedAssignment?.title}</p>
              </div>
              <button
                onClick={() => {
                  setShowSubmissionsModal(false);
                  setSelectedAssignment(null);
                  setAssignmentSubmissions([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingSubmissions ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 mx-auto mb-4 animate-spin" />
                <p className="text-gray-600">Loading submissions...</p>
              </div>
            ) : assignmentSubmissions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Submissions Yet</h3>
                <p className="text-gray-600">Students haven't submitted this assignment yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary Statistics */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Submission Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{assignmentSubmissions.length}</div>
                      <div className="text-sm text-gray-600">Total Submissions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {assignmentSubmissions.filter(s => !isSubmissionLate(s.submittedAt, selectedAssignment?.dueDate)).length}
                      </div>
                      <div className="text-sm text-gray-600">On Time</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {assignmentSubmissions.filter(s => isSubmissionLate(s.submittedAt, selectedAssignment?.dueDate)).length}
                      </div>
                      <div className="text-sm text-gray-600">Late Submissions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-600">{selectedAssignment?.maxMarks || 'N/A'}</div>
                      <div className="text-sm text-gray-600">Max Marks</div>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-600 text-center">
                    Due Date: {selectedAssignment?.dueDate}
                  </div>
                </div>

                {/* Submissions List */}
                <div className="space-y-4">
                  {assignmentSubmissions.map((submission) => {
                    const isLate = isSubmissionLate(submission.submittedAt, selectedAssignment?.dueDate);
                    
                    return (
                      <div key={submission.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {submission.studentName || 'Unknown Student'}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {submission.studentEmail || 'No email'}
                                </p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                isLate ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {isLate ? 'Late Submission' : 'On Time'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                Submitted: {formatSubmissionDate(submission.submittedAt)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Submission Content */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-3">
                          <h5 className="font-medium text-gray-900 mb-2">Submission Text:</h5>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {submission.submissionText || 'No text submitted'}
                          </p>
                        </div>

                        {/* Submission Files */}
                        {submission.submissionFiles && submission.submissionFiles.length > 0 && (
                          <div className="mb-3">
                            <h5 className="font-medium text-gray-900 mb-2">Attached Files:</h5>
                            <div className="space-y-2">
                              {submission.submissionFiles.map((file, index) => (
                                <div key={index} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                                  <div className="flex items-center">
                                    <FileText className="w-4 h-4 text-blue-600 mr-2" />
                                    <span className="text-sm text-gray-900">{file.name}</span>
                                  </div>
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    Download
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BatchDetailsModalEnhanced;
